import type { IAdditional } from 'additional'
import { defineStore } from 'pinia'
import { reorderArrayWithContactUsLast } from '@ui/helpers/functions'
import {
  useHotelStore,
} from 'pinia-store'
import { getDataLang } from '../helpers/api'

const DATA = {
  page_title: '',
  page_subtitle: '',
  page_description: '',
  page_image: '',
  page_id: '',
  slug: '',
}

export interface IPagesParams {
  page_id: string
  page_title: string
  page_subtitle: string
  page_image: string
  page_meta_title: string
  page_meta_description: string
  page_menu_title: string
  title: string
  slug: string
  page_order: number
  is_navbar: number
  is_footer: number
  is_spa: number
  refirect_url?: string
}

export interface IPagesMenuParams {
  title: string
  slug: string
  is_navbar: number
  is_footer: number
  is_spa: number
  refirect_url?: string
  child?: string
}

export interface PagesState {
  list: Array<IPagesParams>
  loading: boolean
  menu: Array<Pick<IPagesParams, 'title' | 'slug'>>
  menuFooter: Array<Pick<IPagesParams, 'title' | 'slug'>>
  pages: any
}

export const PAGE_PARAMS = {
  page: 1,
  page_size: 6,
}

export const PAGINATION_PARAMS = {
  ...PAGE_PARAMS,
  total_page: 0,
  total_data: 0,
}

function handleMenu(tmp, tmpAdditional, func) {
  if (Array.isArray(tmp) && tmp.length) {
    const menu = tmp
      .filter((el: IPagesParams) => el.is_navbar)
      .map(({ slug, page_menu_title: title, is_navbar, is_footer, is_spa }: IPagesParams) => ({
        slug,
        title,
        is_navbar,
        is_footer,
        is_spa,
      }))
    const menuFooter = tmp
      .filter((el: IPagesParams) => el.is_footer && el.slug && el.slug !== 'home')
      .map(({ slug, page_menu_title: title, is_navbar, is_footer }: IPagesParams) => ({
        slug,
        title,
        is_navbar,
        is_footer,
      }))
    if (tmpAdditional && tmpAdditional.length) {
      // eslint-disable-next-line array-callback-return
      tmpAdditional.map((el: IAdditional) => {
        if (el?.slug && el?.status && el?.is_footer) {
          menuFooter.push({
            slug: 'additional-slug',
            child: el.slug,
            is_navbar: el.is_navbar,
            is_footer: el.is_footer,
            title: el?.title || el?.name,
          })
        }
        if (el?.slug && el?.status && el?.is_navbar) {
          menu.push({
            slug: 'additional-slug',
            child: el.slug,
            is_navbar: el.is_navbar,
            is_footer: el.is_footer,
            title: el?.title || el?.name,
            is_spa: 0,
          })
        }
      })
    }
    func.setMenu(menu)
    func.setMenuFooter(menuFooter)
  }
}

export const usePagesStore = defineStore('pages', {
  state: (): PagesState => ({
    list: [],
    menu: [],
    menuFooter: [],
    loading: false,
    pages: [],
  }),
  getters: {
    getMenu(): PagesState['menu'] {
      return reorderArrayWithContactUsLast(getDataLang(this.menu))
    },
    getMenuFooter(): PagesState['menuFooter'] {
      return reorderArrayWithContactUsLast(getDataLang(this.menuFooter))
    },
    getList(): PagesState['list'] {
      return getDataLang(this.list)
    },
    getProfile(state): any {
      const list = getDataLang(state.list)
      return (slug: string) => Array.isArray(list)
        && list.length
        ? list.find((el: any) => el.slug === slug)
        : DATA
    },
  },
  actions: {
    setList(data: IPagesParams[], dataAdditional?: IAdditional[]) {
      const tmp = getDataLang(data)
      const tmpAdditional = getDataLang(dataAdditional)
      const useHotel = useHotelStore()
      const profile = useHotel.getProfile
      this.list = tmp
      if (data && typeof data === 'object'
        && !Array.isArray(data) && Object.keys(data).length) {
        const menuFooter: any = {}
        const menuNavbar: any = {}
        for (let i = 0; i < Object.entries(data).length; i++) {
          const el: any = Object.entries(data)[i]
          if (!menuFooter[el[0]])
            menuFooter[el[0]] = []

          if (!menuNavbar[el[0]])
            menuNavbar[el[0]] = []

          if (el[0]) {
            for (let k = 0; k < el[1].length; k++) {
              const obj = el[1][k]
              const tmpObj = {
                slug: obj?.slug,
                title: obj?.page_menu_title,
                page_title: obj?.page_title,
                is_navbar: obj?.is_navbar,
                is_footer: obj?.is_footer,
                is_spa: obj?.is_spa,
              }
              if (obj.is_footer && obj.slug && (obj.slug !== 'home' || ([139, 136, 140].includes(profile?.hotel_id))))
                menuFooter[el[0]].push({ ...tmpObj })

              if (obj.is_navbar && obj.slug)
                menuNavbar[el[0]].push({ ...tmpObj })
            }
          }
          // additional
          if (tmpAdditional && Array.isArray(tmpAdditional) && tmpAdditional.length) {
            for (let k = 0; k < tmpAdditional.length; k++) {
              const objAdditional = tmpAdditional[k]
              const tmpObjAdditional = {
                slug: 'additional-slug',
                page_title: objAdditional?.page_title || objAdditional?.title,
                child: objAdditional?.slug,
                is_navbar: Number(objAdditional?.is_navbar) === 1,
                is_footer: Number(objAdditional?.is_footer) === 1,
                is_spa: Number(objAdditional?.is_spa) === 1,
                title: objAdditional?.title || objAdditional?.name,
                redirect_url: objAdditional?.redirect_url,
              }
              if (objAdditional.is_footer && objAdditional.slug && objAdditional.slug !== 'home')
                menuFooter[el[0]].push({ ...tmpObjAdditional })

              if (objAdditional.is_navbar && objAdditional.slug)
                menuNavbar[el[0]].push(tmpObjAdditional)
            }
          }
        }
        this.setMenu(menuNavbar)
        this.setMenuFooter(menuFooter)
      }
      else {
        handleMenu(data, tmpAdditional, this)
      }
    },
    setMenu(data: Array<Pick<IPagesMenuParams, 'title' | 'slug'>>) {
      this.menu = data
    },
    setMenuFooter(data: Array<Pick<IPagesMenuParams, 'title' | 'slug'>>) {
      this.menuFooter = data
    },
  },
})
